// ============================================================================
// Active property: central store for header property switcher (owner/team admin).
// Persists to localStorage; syncs from URL (?property_id= or /properties/:id).
// Cleaners do not use this.
// ============================================================================

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

const STORAGE_KEY = "active_property_id";

function getStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function setStored(id: string) {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {}
}

export interface ActivePropertyContextValue {
  activePropertyId: string | null;
  setActivePropertyId: (id: string | null) => void;
}

const ActivePropertyContext = createContext<ActivePropertyContextValue | null>(null);

export function useActiveProperty(): ActivePropertyContextValue | null {
  return useContext(ActivePropertyContext);
}

interface ActivePropertyProviderProps {
  children: ReactNode;
}

export function ActivePropertyProvider({ children }: ActivePropertyProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePropertyId, setState] = useState<string | null>(getStored);

  const setActivePropertyId = useCallback((id: string | null) => {
    setState(id);
    if (id) setStored(id);
    else {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  }, []);

  // Sync from URL into context + storage
  useEffect(() => {
    const fromPath = location.pathname.match(/^\/properties\/([^/]+)/)?.[1] ?? null;
    const fromQuery = new URLSearchParams(location.search).get("property_id");
    const fromUrl = fromPath ?? fromQuery;
    if (fromUrl && fromUrl !== activePropertyId) {
      setState(fromUrl);
      setStored(fromUrl);
    }
  }, [location.pathname, location.search]);

  const value = useMemo(
    () => ({ activePropertyId, setActivePropertyId }),
    [activePropertyId, setActivePropertyId]
  );

  return (
    <ActivePropertyContext.Provider value={value}>
      {children}
    </ActivePropertyContext.Provider>
  );
}
